import { X, Download, Award } from "lucide-react";
import { printCertificate } from "../lib/report";

export function Certificate({ report, onClose }) {
  if (!report) return null;
  const { name, code, logo, baseline, post } = report;
  const b = baseline.weighted_total;
  const p = post.weighted_total;
  const diff = Math.round((p - b) * 10) / 10;
  const improved = diff > 0;
  const relIncrease = improved && b > 0 ? Math.round((diff / b) * 1000) / 10 : null;
  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  const gains = baseline.competencies
    .map((c, i) => {
      const pc = post.competencies[i];
      if (c.pct == null || pc?.pct == null) return null;
      return { label: c.label, change: Math.round((pc.pct - c.pct) * 10) / 10 };
    })
    .filter((g) => g && g.change > 0)
    .sort((a, b2) => b2.change - a.change)
    .slice(0, 3);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-[#1E293B]/50 p-4 sm:p-8" onClick={onClose}>
      <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="no-print mb-3 flex items-center justify-end gap-2">
          <button onClick={printCertificate} data-testid="print-certificate-btn"
            className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
            <Download className="h-4 w-4" strokeWidth={1.5} /> Download certificate
          </button>
          <button onClick={onClose} data-testid="close-certificate-btn" className="rounded-full bg-white p-2.5 text-[#1E293B]/70 hover:text-[#1E293B]"><X className="h-5 w-5" /></button>
        </div>

        {/* Certificate */}
        <div data-certificate className="relative bg-white p-3">
          <div className="border-[3px] border-[#1E293B] p-2">
            <div className="border border-[#2563EB] px-8 py-12 text-center sm:px-14">
              {logo ? (
                <img src={logo} alt="Program logo" className="mx-auto mb-6 max-h-20 object-contain" />
              ) : (
                <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#1E293B] text-[#F1F5F9]">
                  <Award strokeWidth={1.5} className="h-7 w-7" />
                </div>
              )}

              <div className="text-xs uppercase tracking-[0.35em] text-[#2563EB]">Study Abroad Global Experience Certification</div>
              <h1 className="mt-3 font-display text-4xl tracking-tight text-[#1E293B] sm:text-5xl">Certificate of Achievement</h1>
              <div className="mx-auto mt-5 h-px w-24 bg-[#E2E8F0]" />

              <p className="mt-8 text-sm uppercase tracking-[0.2em] text-[#1E293B]/50">This is proudly presented to</p>
              <p className="mt-3 font-display text-4xl text-[#1E293B]" data-testid="certificate-name">{name || "Participant"}</p>

              <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-[#1E293B]/75">
                for completing the Study Abroad Global Experience Certification assessment across all eight sections
                {improved ? (
                  <> and improving their overall Global Competence score by</>
                ) : (
                  <> and achieving an overall Global Competence score of</>
                )}
              </p>

              <div className="mt-6 flex items-end justify-center gap-3">
                <span className="font-display text-6xl text-[#2563EB]" data-testid="certificate-diff">
                  {improved && relIncrease != null ? `+${relIncrease}%` : (improved ? `+${diff}` : p)}
                </span>
                <span className="mb-2 text-lg text-[#1E293B]/50">
                  {improved && relIncrease != null ? "increase" : (improved ? "points" : "/ 100")}
                </span>
              </div>
              <p className="mt-2 text-sm text-[#1E293B]/60">
                {improved
                  ? `+${diff} points · growth from ${b} to ${p} out of 100 (baseline → post)`
                  : `Final weighted score based on completed sections`}
              </p>

              {gains.length > 0 && (
                <div className="mx-auto mt-8 max-w-xl" data-testid="certificate-gains">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#1E293B]/50">Strongest areas of growth</div>
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    {gains.map((g) => (
                      <span key={g.label} className="rounded-full border border-[#2563EB]/40 bg-[#2563EB]/5 px-4 py-1.5 text-sm text-[#1E293B]">
                        {g.label} <span className="font-semibold text-[#2563EB]">+{g.change}%</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-12 flex items-end justify-between">
                <div className="text-left">
                  <img src="/gcrc-logo.png" alt="Global Competency Research Consortium" className="h-40 w-40 object-contain" data-testid="certificate-seal" />
                  <div className="mt-2 text-xs uppercase tracking-[0.15em] text-[#1E293B]/50">Global Competency<br />Research Consortium</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg text-[#1E293B]">{today}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.15em] text-[#1E293B]/50">Date · {code}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
